import { Button, Input, Popover, Table, Tabs, Tag, notification } from "antd";
import axios from "axios";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useDarkModeClass from "../../../hooks/useDarkModeClass";
import { refresh } from "../../../store/adminSlice";

const { TabPane } = Tabs;

export default function ShippingTable() {
  const { t } = useTranslation();
  // State for storing data, pagination and active tab
  const [orderShippingData, setOrderShippingData] = useState([]);
  const [vnlinOrdersData, setVnlinOrdersData] = useState([]);
  const [vnlinPackagesData, setVnlinPackagesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [wegeOrderId, setWegeOrderId] = useState("");
  const [debouncedWegeOrderId, setDebouncedWegeOrderId] = useState(wegeOrderId);
  const [visiblePopoverId, setVisiblePopoverId] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDarkMode = useDarkModeClass();

  // Pagination state for each table
  const [orderShippingPagination, setOrderShippingPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [vnlinOrdersPagination, setVnlinOrdersPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [vnlinPackagesPagination, setVnlinPackagesPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // Handle API errors, handle refresh if necessary, etc.
  const handleApiError = async (e, refresh, tab, pagination, retryFunction) => {
    if (e.response?.status === 401 && Cookies.get("logss")) {
      try {
        await dispatch(refresh());
        retryFunction(tab, pagination);
      } catch (refreshError) {
        setLoading(false);
      }
    } else if (e.response?.status === 401 && !Cookies.get("logss")) {
      navigate("/");
    } else {
      setLoading(false);
    }
  };

  const fetchData = async (tab, pagination) => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
      };

      if (debouncedWegeOrderId) {
        params.wege_order_id = debouncedWegeOrderId;
      }

      if (tab === "1") {
        const res = await axios.get(`/shipping/api/v1/order-shipping/`, {
          params,
        });
        setOrderShippingData(res.data.results);
        setOrderShippingPagination({
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: res.data.count,
        });
      } else if (tab === "2") {
        const res = await axios.get(`/shipping/api/v1/vnlin-orders/`, {
          params,
        });
        setVnlinOrdersData(res.data.results);
        setVnlinOrdersPagination({
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: res.data.count,
        });
      } else if (tab === "3") {
        const res = await axios.get(`/shipping/api/v1/vnlin-packages/`, {
          params,
        });
        setVnlinPackagesData(res.data.results);
        setVnlinPackagesPagination({
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: res.data.count,
        });
      }
    } catch (e) {
      handleApiError(e, refresh, fetchData, tab, pagination, fetchData);
    } finally {
      setLoading(false);
    }
  };

  // Set up debouncing with useEffect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedWegeOrderId(wegeOrderId);
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [wegeOrderId]);

  const handleDelete = async (id, tab) => {
    try {
      if (tab === "1") {
        await axios.delete(`/shipping/api/v1/order-shipping/${id}/`);
      } else if (tab === "2") {
        await axios.delete(`/shipping/api/v1/vnlin-orders/${id}/`);
      } else if (tab === "3") {
        await axios.delete(`/shipping/api/v1/vnlin-packages/${id}/`);
      }
      notification.success({ message: "Deleted successfully" });
      setVisiblePopoverId(null); // Hide the popover after deletion
      fetchData(tab, {
        current: orderShippingPagination.current,
        pageSize: orderShippingPagination.pageSize,
      });
    } catch (e) {
      handleApiError(
        e,
        refresh,
        fetchData,
        tab,
        orderShippingPagination,
        handleDelete,
      );
    }
  };

  const showDeleteConfirm = (id, tab) => {
    return (
      <Popover
        content={
          <div>
            <p>Are you sure you want to delete this item?</p>
            <Button onClick={() => handleDelete(id, tab)} type="primary">
              Yes, Delete
            </Button>
            <Button
              onClick={() => setVisiblePopoverId(null)} // Hide popover when cancelled
              style={{ marginLeft: 8 }}
            >
              Cancel
            </Button>
          </div>
        }
        title="Confirmation"
        trigger="click"
        open={visiblePopoverId === id} // Only show the popover for the clicked row
        onOpenChange={(visible) => {
          if (!visible) {
            setVisiblePopoverId(null); // Hide popover when closed
          }
        }}
      >
        <Button type="danger" onClick={() => setVisiblePopoverId(id)}>
          Delete
        </Button>
      </Popover>
    );
  };

  // Columns for Order Shipping table
  const orderShippingColumns = [
    { title: t("shipping.columns.id", "ID"), dataIndex: "id" },
    {
      title: t("table.columns.wegeOrder", "Wege Order Id"),
      dataIndex: "wege_order_id",
    },
    {
      title: t("table.columns.provider", "Provider"),
      dataIndex: "provider",
    },
    {
      title: t("table.columns.trackingNumber", "Tracking Number"),
      dataIndex: "tracking_number",
    },
    {
      title: t("table.columns.status", "Status"),
      dataIndex: "status",
      key: "status",
      render: (status) => {
        if (!status) return "-";
        const lower = status.toLowerCase();
        let color = "default";
        if (lower === "arrived") color = "success";
        else if (lower === "returned") color = "warning";
        else if (lower === "lostconfiscated" || lower === "lost_confiscated")
          color = "error";
        else if (lower === "tracking" || lower === "shipping")
          color = "processing";
        return (
          <Tag color={color}>
            {t(`order.status.${lower}`, status)}
          </Tag>
        );
      },
    },
  ];

  // Columns for Vnlin Orders table
  const vnlinOrdersColumns = [
    { title: t("shipping.columns.id", "ID"), dataIndex: "id" },
    {
      title: t("table.columns.wegeOrder", "Wege Order Id"),
      dataIndex: "wege_order_id",
    },
    {
      title: t("shipping.columns.vnlinOrderId", "Vnlin Order ID"),
      dataIndex: "vnlin_order_id",
    },
    {
      title: t("table.columns.status", "Status"),
      dataIndex: "status",
    },
  ];

  // Columns for Vnlin Packages table
  const vnlinPackagesColumns = [
    { title: t("shipping.columns.id", "ID"), dataIndex: "id" },
    {
      title: t("table.columns.wegeOrder", "Wege Order Id"),
      dataIndex: "wege_order_id",
    },
    {
      title: t("shipping.columns.packageId", "Package ID"),
      dataIndex: "package_id",
    },
    {
      title: t("table.columns.status", "Status"),
      dataIndex: "status",
    },
  ];

  useEffect(() => {
    fetchData(activeTab, {
      current: orderShippingPagination.current,
      pageSize: orderShippingPagination.pageSize,
    });
  }, [activeTab, debouncedWegeOrderId]);

  const onTabChange = (key) => {
    setActiveTab(key);
    fetchData(key, { current: 1, pageSize: 10 });
  };

  const onPageChange = (pagination) => {
    if (activeTab === "1") {
      setOrderShippingPagination(pagination);
      fetchData("1", pagination);
    } else if (activeTab === "2") {
      setVnlinOrdersPagination(pagination);
      fetchData("2", pagination);
    } else if (activeTab === "3") {
      setVnlinPackagesPagination(pagination);
      fetchData("3", pagination);
    }
  };

  const handleSearchChange = (e) => {
    setWegeOrderId(e.target.value); // This will trigger useEffect debounce
  };

  return (
    <div>
      {/* Search Input above the Table */}
      <div className="d-flex justify-content-end">
        <Input
          placeholder={t("searchByWegeOrder", "Search by Wege Order ID")}
          value={wegeOrderId}
          onChange={handleSearchChange}
          onPressEnter={() =>
            fetchData(activeTab, { current: 1, pageSize: 10 })
          }
          className="search-input mt-2 w-25 text-right"
        />
      </div>
      <Tabs activeKey={activeTab} onChange={onTabChange}>
        <TabPane tab={t("shipping.orderShipping", "Order Shipping")} key="1">
          <Table
            columns={orderShippingColumns}
            dataSource={orderShippingData}
            loading={loading}
            pagination={orderShippingPagination}
            onChange={onPageChange}
            rowKey="id"
            className={isDarkMode ? "dark-ant-table" : ""}
          />
        </TabPane>

        <TabPane tab={t("shipping.vnlinOrders", "Vnlin Orders")} key="2">
          <Table
            columns={vnlinOrdersColumns}
            dataSource={vnlinOrdersData}
            loading={loading}
            pagination={vnlinOrdersPagination}
            onChange={onPageChange}
            rowKey="id"
            className={isDarkMode ? "dark-ant-table" : ""}
          />
        </TabPane>

        <TabPane tab={t("shipping.vnlinPackages", "Vnlin Packages")} key="3">
          <Table
            columns={vnlinPackagesColumns}
            dataSource={vnlinPackagesData}
            loading={loading}
            pagination={vnlinPackagesPagination}
            onChange={onPageChange}
            rowKey="id"
            className={isDarkMode ? "dark-ant-table" : ""}
          />
        </TabPane>
      </Tabs>
    </div>
  );
}
